import { useSetState } from 'ahooks';
import { useEffect, useState } from 'react';
import { useCommunityInteraction, usePostReferenceFeed } from '@/app/components/shared/Community';
import { useFetch } from '@/app/hooks';
import {
  apiGetMyInspirationSubmissions,
  apiGetMyShortFilmSubmissions,
  apiGetMyTutorialSubmissions,
  apiGetOthersInspirationSubmissions,
  apiGetOthersShortFilmSubmissions,
  apiGetOthersTutorialSubmissions,
  materialsGetMyShareElements,
  materialsGetPersonaShareElements,
} from '../apis';
import { GUEST_PROFILE_PREVIEW_PAGE_SIZE, PAGE_SIZE, shouldLimitGuestProfileModules } from '../constant';
import type { MaterialsGetMyShareElementsData, ReferenceFetchDataResult } from '../types';
import { useFetchData } from './use-fetch-data';

/**
 * 管理 Published 组件的数据获取和加载状态
 */
export const usePublishedData = (userId?: string) => {
  const shouldLimitPreview = shouldLimitGuestProfileModules(userId);
  const pageSize = shouldLimitPreview ? GUEST_PROFILE_PREVIEW_PAGE_SIZE : PAGE_SIZE;
  const [postReferenceState, setPostReferenceState] = useSetState({
    page: 0,
    total: 0,
    hasMore: false,
  });
  const updatePageInfo = ({ total, token }: { total?: number; token?: string }) => {
    setPostReferenceState({
      total: shouldLimitPreview ? GUEST_PROFILE_PREVIEW_PAGE_SIZE : total || 0,
      hasMore: !shouldLimitPreview && !!token,
    });
  };
  const [pageLoading, setPageLoading] = useState(true);

  const shortFilmData = useFetchData({
    userId,
    fetchData: userId ? apiGetOthersShortFilmSubmissions : apiGetMyShortFilmSubmissions,
  });

  const tutorialData = useFetchData({
    userId,
    fetchData: userId ? apiGetOthersTutorialSubmissions : apiGetMyTutorialSubmissions,
  });

  const inspirationData = useFetchData({
    userId,
    fetchData: userId ? apiGetOthersInspirationSubmissions : apiGetMyInspirationSubmissions,
  });

  const fetchPostReference = useFetch(
    (params: { query: MaterialsGetMyShareElementsData['query'] }) => {
      if (userId) {
        // 获取客态的个人主页主体
        return materialsGetPersonaShareElements({
          query: params.query,
          path: {
            user_id: userId,
          },
        });
      }
      // 获取我的主体投稿
      return materialsGetMyShareElements({
        query: params.query,
      });
    },
    {
      manual: true,
    },
  );

  const {
    referenceList,
    getShareReferenceData,
    handleDetail: handleShareReferenceDetail,
    updateShareReferenceInteractionStatus,
    onExpose,
  } = usePostReferenceFeed({
    handleEnter: () => {},
    handleLeave: () => {},
    updatePageInfo: updatePageInfo,
    pageToken: undefined,
    fetchMoreMode: 'page',
    pageSize,
    fromPage: userId ? 'guest_profile' : 'my_published_page',
    fetchData: fetchPostReference.runAsync,
  });

  const shareReferenceLoading = fetchPostReference.loading;

  const { vote, unVote, collect, unCollect } = useCommunityInteraction({
    onInteractionStatusChange: updateShareReferenceInteractionStatus,
  });

  useEffect(() => {
    getShareReferenceData({ page: postReferenceState.page });
  }, [postReferenceState.page]);

  // 监听加载状态
  useEffect(() => {
    const isLoading =
      (shortFilmData.fetchReq.loading && !shortFilmData.state.list?.length) ||
      (tutorialData.fetchReq.loading && !tutorialData.state.list?.length) ||
      (shareReferenceLoading && !referenceList.length) ||
      (inspirationData.fetchReq.loading && !inspirationData.state.list?.length);

    setPageLoading(isLoading);
  }, [
    shortFilmData.fetchReq.loading,
    shortFilmData.state.list?.length,
    tutorialData.fetchReq.loading,
    tutorialData.state.list?.length,
    shareReferenceLoading,
    referenceList?.length,
    inspirationData.fetchReq.loading,
    inspirationData.state.list?.length,
  ]);

  // 检查是否有数据
  const hasData =
    shortFilmData.state.list.length > 0 ||
    tutorialData.state.list.length > 0 ||
    referenceList.length > 0 ||
    inspirationData.state.list.length > 0;

  const referenceData: ReferenceFetchDataResult = {
    state: {
      page: postReferenceState.page,
      list: referenceList,
      total: postReferenceState.total,
      isLoading: shareReferenceLoading,
      hasMore: postReferenceState.hasMore,
    },
    setState: setPostReferenceState,
    handleDetail: handleShareReferenceDetail,
    vote,
    unVote,
    collect,
    unCollect,
    onExpose,
    fetchReq: fetchPostReference,
  };

  return {
    shortFilmData,
    tutorialData,
    inspirationData,
    referenceData,
    pageLoading: pageLoading,
    hasData,
  };
};
